import { useState } from "react";
import { IResolution } from "../types/interfaces/IResolution";
import { Box, Stack } from '@mui/material';
import ResolutionsSelect from "./ResolutionsSelect";
import ResolutionDisplay from "./ResolutionDisplay";
import ResolutionsEventTitle from "./ResolutionsEventTitle";
import ResolutionVote from "./ResolutionVote";
import ResolutionComment from "./ResolutionComment";
import ResolutionSubmit from "./ResolutionSubmit";
import { useResolutionsContext } from "../hooks/useResolutionsContext";
import useSubmitResponse from "../hooks/useSubmitResponse";

//This component is responsible for handling the resolution state and logic, and allowing a user to vote.
export default function ResolutionsContainer() {
    const [selectedResolution, setSelectedResolution] = useState<IResolution | null>(null);
    const [supportFlag, setSupportFlag] = useState<boolean | null>(null);
    const [comment, setComment] = useState<string | null>(null);
    const [initialSupportFlag, setInitialSupportFlag] = useState<boolean | null>(null);
    const [initialComment, setInitialComment] = useState<string | null>(null);
    const [submitEnabled, setSubmitEnabled] = useState<boolean>(false);

    const { resolutionEvents, selectedEvent, cstKey, setResolutionEvents, setSelectedEvent } = useResolutionsContext();

    const API_RESOLUTIONS = process.env.REACT_APP_API_RESOLUTIONS;
    let apiSaveResolutionResponses = `${API_RESOLUTIONS}${cstKey}`;
    const submitResponse = useSubmitResponse(apiSaveResolutionResponses);

    //Function to handle a resolution selection.
    const resolutionChange = (resolution: IResolution | null) => {
        if (resolution) {
            setSelectedResolution(resolution);
            setSupportFlag(resolution.ResolutionResponse.SupportsFlag);
            setComment(resolution.ResolutionResponse.Comment);
            setInitialSupportFlag(resolution.ResolutionResponse.SupportsFlag);
            setInitialComment(resolution.ResolutionResponse.Comment);

            if (resolution.ResolutionResponse.Key === null && resolution.ResolutionResponse.SupportsFlag) {
                setSubmitEnabled(true);
            } else {
                setSubmitEnabled(false);
            }
        }
        else {
            setSelectedResolution(null);
            setSupportFlag(null);
            setComment(null);
            setInitialSupportFlag(null);
            setInitialComment(null);
            setSubmitEnabled(false);
        }
    };

    //Function to handle a resolution response supports flag change.
    const supportFlagChange = (value: boolean) => {
        setSupportFlag(value);
        if ((selectedResolution?.ResolutionResponse.Key === null && initialSupportFlag) || (value !== initialSupportFlag || comment !== initialComment) && !(value === null && comment === '')) {
            setSubmitEnabled(true);
        }
        else {
            setSubmitEnabled(false);
        }
    };

    //Function to handle a resolution response comment change.
    const commentChange = (value: string) => {
        setComment(value);
        if ((supportFlag !== initialSupportFlag || value !== initialComment)
            && !(supportFlag === null && value === '')) {
            setSubmitEnabled(true);
        }
        else {
            setSubmitEnabled(false);
        }
    };

    //Function to handle a resolution response submission.
    const submitClick = async () => {
        if (resolutionEvents && selectedEvent && selectedResolution && ((selectedResolution.ResolutionResponse.Key === null && initialSupportFlag) || (supportFlag !== initialSupportFlag || comment !== initialComment))) {
            const resolutionEventsToSubmit = resolutionEvents.map(event => {
                if (event.Key === selectedEvent.Key) {
                    return {
                        ...event, Resolutions: event.Resolutions.map((resolution: IResolution) => {
                            if (resolution.Number === selectedResolution.Number) {
                                return {
                                    ...resolution, ResolutionResponse: { ...resolution.ResolutionResponse, IsModified: true, SupportsFlag: supportFlag, Comment: comment }
                                };
                            }
                            return resolution;
                        })
                    };
                }
                return event;
            });
            const savedResolutionEvents = await submitResponse(resolutionEventsToSubmit);
            setResolutionEvents(savedResolutionEvents);
            setSelectedEvent(savedResolutionEvents[0]);
            setSelectedResolution(null);
            setSupportFlag(null);
            setComment(null);
        }
    };

    return (
        <Box>
            <ResolutionsEventTitle />
            <ResolutionsSelect onSelectResolution={resolutionChange} />
            {selectedResolution && (
                <Stack spacing={1}>
                    <ResolutionDisplay selectedResolution={selectedResolution} />
                    <ResolutionVote supportFlag={supportFlag} onSupportFlagChange={supportFlagChange} />
                    <ResolutionComment comment={comment} onCommentChange={commentChange} />
                    <ResolutionSubmit submitEnabled={submitEnabled} onSubmitClick={submitClick} />
                </Stack>
            )}
        </Box>
    );
}